import moment from 'moment'
import { getBillingDynamicsInfo } from './getBillingDynamicsInfo'
import { isDateInPast } from 'Rentlio/helper/isDateInPast'
import { FULL_AMOUNT_WILL_BE_PAID_ENUM } from 'Rentlio/utils/cancellationPolicyEnums'
import { formatPrice } from 'Rentlio/components/utils/Price/price'
import { BE_BANK_TRANSFER } from 'Rentlio/utils/paymentTypesEnum'
import { FIRST_NIGHT } from 'Rentlio/utils/CancellationPolicyFeeTypeEnum'
import { logErrorWithJsonData } from 'Rentlio/utils/logging'

export const getDaysBeforeArrivalTime = (arrival, daysBefore, translate, dateOnly) => {
  const dateBefore = arrival
    .clone()
    .hours(0)
    .subtract(daysBefore, 'days')
    .subtract(dateOnly ? 0 : 1, 'seconds')

  return dateOnly
    ? dateBefore.format('DD.MM.YYYY')
    : `${dateBefore.format('DD.MM.YYYY.')} ${translate('Until')} ${dateBefore.format('HH:mm')}`
}

export const getAccommodationCancelationPolicyText = (
  translate,
  currencyConversion,
  totalRatePrice,
  unitTypeCurrency,
  policy,
  arrivalDate,
  createdAt,
  firstNightPrice
) => {
  if (!policy) {
    return
  }

  const {
    daysBeforeArrival,
    canGuestCancelFreeOfCharge,
    cancellationFeePercentageBefore,
    cancellationFeePercentageAfter,
    cancellationFeeBeforeType,
    cancellationFeeAfterType
  } = policy

  const arrival = moment.utc(arrivalDate * 1000)
  const { value, currency } = currencyConversion(totalRatePrice, unitTypeCurrency)

  const isTresholdDateInThePast = isDateInPast(
    arrival.clone().subtract(daysBeforeArrival ? +daysBeforeArrival : 0, 'days'),
    true,
    createdAt
  )

  let cancellationPolicyText = ''

  const cancellationFeeAfter =
    cancellationFeeAfterType === FIRST_NIGHT ? firstNightPrice : value * (cancellationFeePercentageAfter / 100)

  if (canGuestCancelFreeOfCharge) {
    if (daysBeforeArrival === 0) {
      cancellationPolicyText = translate('You can cancel your reservation free of charge at any time')
    }

    if (daysBeforeArrival && daysBeforeArrival > 0) {
      // handle case when arrivalDate - daysBeforeArrival is in the past
      cancellationPolicyText = isTresholdDateInThePast
        ? `${translate('If you cancel reservation you will be charged amount', {
            '%amount%': formatPrice(cancellationFeeAfter),
            '%currency%': currency || ''
          })}`
        : // handle case when arrivalDate - daysBeforeArrival is in the future
          `${translate('You can cancel your reservation free of charge before date', {
            '%days%': daysBeforeArrival,
            '%currency%': currency || '',
            '%date%': getDaysBeforeArrivalTime(arrival, daysBeforeArrival, translate)
          })} ${translate('If you cancel reservation after that, you will be charged amount', {
            '%amount%': formatPrice(cancellationFeeAfter),
            '%currency%': currency || ''
          })}`
    }
  }

  if (!canGuestCancelFreeOfCharge) {
    const cancellationFeeBefore =
      cancellationFeeBeforeType === FIRST_NIGHT ? firstNightPrice : value * (cancellationFeePercentageBefore / 100)

    if (daysBeforeArrival === 0) {
      cancellationPolicyText = `${translate('You cannot cancel your reservation free of charge')} ${translate(
        'If you cancel at any time you will be charged',
        {
          '%amount%': formatPrice(cancellationFeeBefore),
          '%currency%': currency || ''
        }
      )}`
    }

    if (daysBeforeArrival && daysBeforeArrival > 0) {
      // handle case when arrivalDate - daysBeforeArrival is in the past
      cancellationPolicyText = isTresholdDateInThePast
        ? `${translate('You cannot cancel your reservation free of charge')} ${translate(
            'If you cancel reservation you will be charged amount',
            {
              '%amount%': formatPrice(cancellationFeeAfter),
              '%currency%': currency || ''
            }
          )}`
        : // handle case when arrivalDate - daysBeforeArrival is in the future
          `${translate('You cannot cancel your reservation free of charge')} ${translate(
            'You will be charged amount if you cancel before date',
            {
              '%amount%': formatPrice(cancellationFeeBefore),
              '%currency%': currency || '',
              '%date%': getDaysBeforeArrivalTime(arrival, daysBeforeArrival, translate)
            }
          )} ${translate('If you cancel reservation after that, you will be charged amount', {
            '%amount%': formatPrice(cancellationFeeAfter),
            '%currency%': currency || ''
          })}`
    }
  }
  return cancellationPolicyText
}

export const getNoShowText = (
  translate,
  currencyConversion,
  totalRatePrice,
  reservationCurrency,
  policy,
  firstNightPrice
) => {
  if (!policy) {
    return
  }

  const { noShowPercentage, noShowFeeType } = policy
  const { value, currency } = currencyConversion(totalRatePrice, reservationCurrency)

  if (noShowPercentage === 0 && noShowFeeType !== FIRST_NIGHT) {
    return translate('You will not be charged in case of no-show')
  }

  if (noShowPercentage > 0 || noShowFeeType === FIRST_NIGHT) {
    const noShowFee = noShowFeeType === FIRST_NIGHT ? firstNightPrice : value * (noShowPercentage / 100)

    return translate('In case of no-show, you will be charged amount', {
      '%amount%': formatPrice(noShowFee),
      '%currency%': currency || ''
    })
  }

  return ''
}

export const getBillingDynamicsText = (
  translate,
  currencyConversion,
  totalRatePrice,
  reservationCurrency,
  policy,
  paymentMethod,
  arrivalDate,
  createdAt,
  firstNightPrice
) => {
  if (!paymentMethod) {
    return ''
  }
  const arrival = moment.utc(arrivalDate * 1000)

  if (paymentMethod !== BE_BANK_TRANSFER) {
    return getBillingDynamicsCreditCardText(
      translate,
      currencyConversion,
      totalRatePrice,
      reservationCurrency,
      policy,
      arrival,
      createdAt,
      firstNightPrice
    )
  }

  return getBillingDynamicsBankTransferText(
    translate,
    currencyConversion,
    totalRatePrice,
    reservationCurrency,
    policy,
    arrival,
    createdAt,
    firstNightPrice
  )
}

export const getBillingDynamicsCreditCardText = (
  translate,
  currencyConversion,
  totalRatePrice,
  reservationCurrency,
  policy,
  arrival,
  createdAt,
  firstNightPrice
) => {
  if (!policy) {
    return
  }

  const {
    prepaymenyAmountPercentage,
    isPrepaymentInThePast,
    total,
    currency,
    fullAmountWillBePaid,
    amountWillBePaidDaysBefore,
    prepaymentAmountDueDate,
    prepayment,
    remainingAmount,
    isAmountWillBePaidDaysBeforeInThePast
  } = getBillingDynamicsInfo(
    currencyConversion,
    totalRatePrice,
    reservationCurrency,
    policy,
    arrival.clone(),
    createdAt,
    firstNightPrice
  )

  // remainingAmount should newer be less than 0 and if it is then we have a bug
  if (remainingAmount < 0) {
    logErrorWithJsonData(
      `remainingAmount in cancellation policy billing dynamics is negative`,
      {
        totalRatePrice,
        reservationCurrency,
        policy,
        arrival: arrival?.format(),
        createdAt,
        firstNightPrice,
        bookingEngine: {
          id: window?.initialBookingEngineData?.bookingEngine?.id,
          slug: window?.initialBookingEngineData?.bookingEngine?.slug
        },
        request: window?.initialBookingEngineData?.request
      },
      {
        critical: 'true',
        description: `Check if the reservation was created and is it correct. If url contains "payment", the reservation is not created.`
      }
    )
  }

  let billingDynamicsText = ''

  const isPrepaymentFullAmount = prepaymenyAmountPercentage === 100 || remainingAmount === 0
  if (isPrepaymentFullAmount && prepaymentAmountDueDate === 0) {
    billingDynamicsText = translate('After confirming the reservation you will be charged amount', {
      '%amount%': formatPrice(total),
      '%currency%': currency || ''
    })
  }

  if (isPrepaymentFullAmount && prepaymentAmountDueDate > 0) {
    billingDynamicsText = isPrepaymentInThePast
      ? translate('After confirming the reservation you will be charged amount', {
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
      : translate('Amount will be charged on', {
          '%amount%': formatPrice(total),
          '%currency%': currency || '',
          '%date%': getDaysBeforeArrivalTime(arrival, prepaymentAmountDueDate, translate, true)
        })
  }

  if (prepaymenyAmountPercentage === 0 && fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.DURING_STAY) {
    billingDynamicsText = translate('Amount will be charged during the stay', {
      '%amount%': formatPrice(total),
      '%currency%': currency || ''
    })
  }

  if (prepaymenyAmountPercentage === 0 && fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL) {
    billingDynamicsText = isAmountWillBePaidDaysBeforeInThePast
      ? translate('After confirming the reservation you will be charged amount', {
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
      : translate('You will be charged amount at date', {
          '%date%': getDaysBeforeArrivalTime(arrival, amountWillBePaidDaysBefore, translate, true),
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.DURING_STAY &&
    prepaymentAmountDueDate === 0 &&
    remainingAmount > 0
  ) {
    billingDynamicsText = translate(
      'You will be charged amount immediately after confirming the reservation. The remaining amount will be charged during your stay',
      {
        '%amount1%': formatPrice(prepayment),
        '%amount2%': formatPrice(remainingAmount),
        '%currency%': currency || '',
        '%currency2%': currency || ''
      }
    )
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.DURING_STAY &&
    prepaymentAmountDueDate > 0 &&
    remainingAmount > 0
  ) {
    billingDynamicsText = isPrepaymentInThePast
      ? `${translate('After confirming the reservation you will be charged amount', {
          '%amount%': formatPrice(prepayment),
          '%currency%': currency || ''
        })} ${translate('The remaining amount will be charged during your stay', {
          '%amount%': formatPrice(remainingAmount),
          '%currency%': currency || ''
        })}`
      : translate('You will be charged amount at date. The remaining amount will be charged during your stay', {
          '%date%': getDaysBeforeArrivalTime(arrival, prepaymentAmountDueDate, translate, true),
          '%amount1%': formatPrice(prepayment),
          '%amount2%': formatPrice(remainingAmount),
          '%currency%': currency || '',
          '%currency2%': currency || ''
        })
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL &&
    prepaymentAmountDueDate === 0 &&
    remainingAmount > 0
  ) {
    billingDynamicsText = isAmountWillBePaidDaysBeforeInThePast
      ? translate('After confirming the reservation you will be charged amount', {
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
      : translate(
          'After confirming the reservation you will be charged amount1. The remaining amount of amount2 will be charged at date',
          {
            '%date%': getDaysBeforeArrivalTime(arrival, amountWillBePaidDaysBefore, translate, true),
            '%amount1%': formatPrice(prepayment),
            '%amount2%': formatPrice(remainingAmount),
            '%currency%': currency || '',
            '%currency2%': currency || ''
          }
        )
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL &&
    prepaymentAmountDueDate > 0 &&
    !isAmountWillBePaidDaysBeforeInThePast &&
    !isPrepaymentInThePast &&
    remainingAmount > 0
  ) {
    billingDynamicsText = translate(
      'You will be charged amount at. The remaining amount of amount2 will be charged at date',
      {
        '%date1%': getDaysBeforeArrivalTime(arrival, prepaymentAmountDueDate, translate, true),
        '%date2%': getDaysBeforeArrivalTime(arrival, amountWillBePaidDaysBefore, translate, true),
        '%amount1%': formatPrice(prepayment),
        '%amount2%': formatPrice(remainingAmount),
        '%currency%': currency || '',
        '%currency2%': currency || ''
      }
    )
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL &&
    prepaymentAmountDueDate > 0 &&
    !isAmountWillBePaidDaysBeforeInThePast &&
    isPrepaymentInThePast &&
    remainingAmount > 0
  ) {
    billingDynamicsText = translate(
      'After confirming the reservation you will be charged amount1. The remaining amount of amount2 will be charged at date',
      {
        '%date%': getDaysBeforeArrivalTime(arrival, amountWillBePaidDaysBefore, translate, true),
        '%amount1%': formatPrice(prepayment),
        '%amount2%': formatPrice(remainingAmount),
        '%currency%': currency || '',
        '%currency2%': currency || ''
      }
    )
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL &&
    prepaymentAmountDueDate > 0 &&
    isAmountWillBePaidDaysBeforeInThePast &&
    isPrepaymentInThePast
  ) {
    billingDynamicsText = translate('After confirming the reservation you will be charged amount', {
      '%amount%': formatPrice(total),
      '%currency%': currency || ''
    })
  }

  if (remainingAmount <= 0 && !billingDynamicsText) {
    billingDynamicsText = isAmountWillBePaidDaysBeforeInThePast
      ? translate('After confirming the reservation you will be charged amount', {
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
      : translate('Amount will be charged on', {
          '%amount%': formatPrice(total),
          '%currency%': currency || '',
          '%date%': getDaysBeforeArrivalTime(arrival, prepaymentAmountDueDate, translate, true)
        })
  }

  return billingDynamicsText
}

export const getBillingDynamicsBankTransferText = (
  translate,
  currencyConversion,
  totalRatePrice,
  reservationCurrency,
  policy,
  arrival,
  createdAt,
  firstNightPrice
) => {
  if (!policy) {
    return
  }

  const {
    prepaymenyAmountPercentage,
    isPrepaymentInThePast,
    total,
    currency,
    fullAmountWillBePaid,
    amountWillBePaidDaysBefore,
    prepaymentAmountDueDate,
    prepayment,
    remainingAmount,
    isAmountWillBePaidDaysBeforeInThePast
  } = getBillingDynamicsInfo(
    currencyConversion,
    totalRatePrice,
    reservationCurrency,
    policy,
    arrival,
    createdAt,
    firstNightPrice
  )

  // remainingAmount should newer be less than 0 and if it is then we have a bug
  if (remainingAmount < 0) {
    logErrorWithJsonData(
      `remainingAmount in cancellation policy billing dynamics is negative`,
      {
        totalRatePrice,
        reservationCurrency,
        policy,
        arrival: arrival?.format(),
        createdAt,
        firstNightPrice,
        bookingEngine: {
          id: window?.initialBookingEngineData?.bookingEngine?.id,
          slug: window?.initialBookingEngineData?.bookingEngine?.slug
        },
        request: window?.initialBookingEngineData?.request
      },
      {
        critical: 'true',
        description: `Check if the reservation was created and is it correct. If url contains "payment", the reservation is not created.`
      }
    )
  }

  let billingDynamicsText = ''

  const isPrepaymentFullAmount = prepaymenyAmountPercentage === 100 || remainingAmount === 0

  if (isPrepaymentFullAmount && prepaymentAmountDueDate === 0) {
    billingDynamicsText = translate('To confirm the reservation we ask you to pay amount now', {
      '%amount%': formatPrice(total),
      '%currency%': currency || ''
    })
  }

  if (isPrepaymentFullAmount && prepaymentAmountDueDate > 0) {
    billingDynamicsText = isPrepaymentInThePast
      ? translate('To confirm the reservation we ask you to pay amount now', {
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
      : translate('To confirm your reservation we ask you to pay amount no later than date', {
          '%date%': getDaysBeforeArrivalTime(arrival, prepaymentAmountDueDate, translate, true),
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
  }

  if (prepaymenyAmountPercentage === 0 && fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.DURING_STAY) {
    billingDynamicsText = translate('We will charge you amount during your stay', {
      '%amount%': formatPrice(total),
      '%currency%': currency || ''
    })
  }

  if (prepaymenyAmountPercentage === 0 && fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL) {
    billingDynamicsText = isAmountWillBePaidDaysBeforeInThePast
      ? translate('To confirm the reservation we ask you to pay amount now', {
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
      : translate('To confirm your reservation we ask you to pay amount no later than date', {
          '%date%': getDaysBeforeArrivalTime(arrival, amountWillBePaidDaysBefore, translate, true),
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.DURING_STAY &&
    prepaymentAmountDueDate === 0 &&
    remainingAmount > 0
  ) {
    billingDynamicsText = `${translate('To confirm the reservation we ask you to pay amount now', {
      '%amount%': formatPrice(prepayment),
      '%currency%': currency || ''
    })} ${translate('The remaining amount will be charged during your stay', {
      '%amount%': formatPrice(remainingAmount),
      '%currency%': currency || ''
    })}`
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.DURING_STAY &&
    prepaymentAmountDueDate > 0 &&
    remainingAmount > 0
  ) {
    billingDynamicsText = `${
      isPrepaymentInThePast
        ? translate('To confirm the reservation we ask you to pay amount now', {
            '%amount%': formatPrice(prepayment),
            '%currency%': currency || ''
          })
        : translate('To confirm your reservation we ask you to pay amount no later than date', {
            '%amount%': formatPrice(prepayment),
            '%currency%': currency || '',
            '%date%': getDaysBeforeArrivalTime(arrival, prepaymentAmountDueDate, translate, true)
          })
    } ${translate('The remaining amount will be charged during your stay', {
      '%amount%': formatPrice(remainingAmount),
      '%currency%': currency || ''
    })}`
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL &&
    prepaymentAmountDueDate === 0 &&
    remainingAmount > 0
  ) {
    billingDynamicsText = isAmountWillBePaidDaysBeforeInThePast
      ? `${translate('To confirm the reservation we ask you to pay amount now', {
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })}`
      : `${translate('To confirm the reservation we ask you to pay amount now', {
          '%amount%': formatPrice(prepayment),
          '%currency%': currency || ''
        })} ${translate('The remaining amount should be paid no later than date', {
          '%date%': getDaysBeforeArrivalTime(arrival, amountWillBePaidDaysBefore, translate, true),
          '%amount%': formatPrice(remainingAmount),
          '%currency%': currency || ''
        })}`
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL &&
    prepaymentAmountDueDate > 0 &&
    !isAmountWillBePaidDaysBeforeInThePast &&
    !isPrepaymentInThePast &&
    remainingAmount > 0
  ) {
    billingDynamicsText = `${translate('To confirm your reservation we ask you to pay amount no later than date', {
      '%amount%': formatPrice(prepayment),
      '%currency%': currency || '',
      '%date%': getDaysBeforeArrivalTime(arrival, prepaymentAmountDueDate, translate, true)
    })} ${translate('The remaining amount should be paid no later than date', {
      '%date%': getDaysBeforeArrivalTime(arrival, amountWillBePaidDaysBefore, translate, true),
      '%amount%': formatPrice(remainingAmount),
      '%currency%': currency || ''
    })}`
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL &&
    prepaymentAmountDueDate > 0 &&
    !isAmountWillBePaidDaysBeforeInThePast &&
    isPrepaymentInThePast &&
    remainingAmount > 0
  ) {
    billingDynamicsText = `${translate('To confirm the reservation we ask you to pay amount now', {
      '%amount%': formatPrice(prepayment),
      '%currency%': currency || ''
    })} ${translate('The remaining amount should be paid no later than date', {
      '%date%': getDaysBeforeArrivalTime(arrival, amountWillBePaidDaysBefore, translate, true),
      '%amount%': formatPrice(remainingAmount),
      '%currency%': currency || ''
    })}`
  }

  if (
    prepayment > 0 &&
    fullAmountWillBePaid === FULL_AMOUNT_WILL_BE_PAID_ENUM.BEFORE_ARRIVAL &&
    prepaymentAmountDueDate > 0 &&
    isAmountWillBePaidDaysBeforeInThePast &&
    isPrepaymentInThePast
  ) {
    billingDynamicsText = `${translate('To confirm the reservation we ask you to pay amount now', {
      '%amount%': formatPrice(total),
      '%currency%': currency || ''
    })}`
  }

  if (remainingAmount <= 0 && !billingDynamicsText) {
    billingDynamicsText = isAmountWillBePaidDaysBeforeInThePast
      ? translate('To confirm the reservation we ask you to pay amount now', {
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
      : translate('To confirm your reservation we ask you to pay amount no later than date', {
          '%date%': getDaysBeforeArrivalTime(arrival, prepaymentAmountDueDate, translate, true),
          '%amount%': formatPrice(total),
          '%currency%': currency || ''
        })
  }

  return billingDynamicsText
}
